// Phone number validation and SMS verification service
// For production, integrate with Twilio, AWS SNS, or similar service

export interface Country {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
}

export interface PhoneNumber {
  countryCode: string;
  number: string;
  formatted: string;
  verified: boolean;
}

export interface SMSVerification {
  id: string;
  phoneNumber: string;
  code: string;
  expiresAt: Date;
  verified: boolean;
}

// Supported countries
export const SUPPORTED_COUNTRIES: Country[] = [
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
  { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸' },
  { code: 'DE', name: 'Germany', dialCode: '+49', flag: '🇩🇪' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'IT', name: 'Italy', dialCode: '+39', flag: '🇮🇹' },
  { code: 'ES', name: 'Spain', dialCode: '+34', flag: '🇪🇸' },
  { code: 'NL', name: 'Netherlands', dialCode: '+31', flag: '🇳🇱' },
  { code: 'BE', name: 'Belgium', dialCode: '+32', flag: '🇧🇪' },
  { code: 'AT', name: 'Austria', dialCode: '+43', flag: '🇦🇹' },
  { code: 'CH', name: 'Switzerland', dialCode: '+41', flag: '🇨🇭' },
  { code: 'SE', name: 'Sweden', dialCode: '+46', flag: '🇸🇪' },
  { code: 'NO', name: 'Norway', dialCode: '+47', flag: '🇳🇴' },
  { code: 'DK', name: 'Denmark', dialCode: '+45', flag: '🇩🇰' },
  { code: 'FI', name: 'Finland', dialCode: '+358', flag: '🇫🇮' },
  { code: 'PL', name: 'Poland', dialCode: '+48', flag: '🇵🇱' },
  { code: 'IE', name: 'Ireland', dialCode: '+353', flag: '🇮🇪' },
];

class PhoneService {
  private static instance: PhoneService;
  private verificationCodes: Map<string, SMSVerification> = new Map();

  static getInstance(): PhoneService {
    if (!PhoneService.instance) {
      PhoneService.instance = new PhoneService();
    }
    return PhoneService.instance;
  }

  // Get all supported countries
  getSupportedCountries(): Country[] {
    return SUPPORTED_COUNTRIES;
  }

  // Get country by dial code
  getCountryByDialCode(dialCode: string): Country | undefined {
    return SUPPORTED_COUNTRIES.find(country => country.dialCode === dialCode);
  }

  // Get country by country code
  getCountryByCode(code: string): Country | undefined {
    return SUPPORTED_COUNTRIES.find(country => country.code === code);
  }

  // Create phone number from country and number
  createPhoneNumber(country: Country, number: string): PhoneNumber | null {
    if (!number || number.trim() === '') {
      return null;
    }

    // Remove any non-digit characters from the number
    const cleanNumber = number.replace(/\D/g, '');
    
    if (!this.validateNumberForCountry(country, cleanNumber)) {
      return null;
    }

    return {
      countryCode: country.dialCode,
      number: cleanNumber,
      formatted: this.formatPhoneNumber(country, cleanNumber),
      verified: false
    };
  }

  // Validate number for specific country
  validateNumberForCountry(country: Country, number: string): boolean {
    switch (country.code) {
      case 'GB':
        // UK: 9-10 digits, starting with 1-9
        return /^[1-9]\d{8,9}$/.test(number);
      case 'US':
        // US: exactly 10 digits
        return /^\d{10}$/.test(number);
      case 'DE':
        // Germany: 10-12 digits
        return /^\d{10,12}$/.test(number);
      case 'FR':
        // France: 9 digits
        return /^\d{9}$/.test(number);
      case 'IT':
        // Italy: 9-10 digits
        return /^\d{9,10}$/.test(number);
      case 'ES':
        // Spain: 9 digits
        return /^\d{9}$/.test(number);
      case 'NL':
        // Netherlands: 9 digits
        return /^\d{9}$/.test(number);
      case 'BE':
        // Belgium: 9 digits
        return /^\d{9}$/.test(number);
      case 'AT':
        // Austria: 10-12 digits
        return /^\d{10,12}$/.test(number);
      case 'CH':
        // Switzerland: 9 digits
        return /^\d{9}$/.test(number);
      case 'SE':
        // Sweden: 9 digits
        return /^\d{9}$/.test(number);
      case 'NO':
        // Norway: 8 digits
        return /^\d{8}$/.test(number);
      case 'DK':
        // Denmark: 8 digits
        return /^\d{8}$/.test(number);
      case 'FI':
        // Finland: 9 digits
        return /^\d{9}$/.test(number);
      case 'PL':
        // Poland: 9 digits
        return /^\d{9}$/.test(number);
      case 'IE':
        // Ireland: 9 digits
        return /^\d{9}$/.test(number);
      default:
        return false;
    }
  }

  // Format phone number for display
  formatPhoneNumber(country: Country, number: string): string {
    switch (country.code) {
      case 'GB':
        return `${country.dialCode} ${number.slice(0, 4)} ${number.slice(4, 7)} ${number.slice(7)}`;
      case 'US':
        return `${country.dialCode} (${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`;
      case 'DE':
        return `${country.dialCode} ${number.slice(0, 3)} ${number.slice(3, 6)} ${number.slice(6)}`;
      case 'FR':
        return `${country.dialCode} ${number.slice(0, 1)} ${number.slice(1, 3)} ${number.slice(3, 5)} ${number.slice(5, 7)} ${number.slice(7)}`;
      case 'IT':
        return `${country.dialCode} ${number.slice(0, 3)} ${number.slice(3, 6)} ${number.slice(6)}`;
      case 'ES':
        return `${country.dialCode} ${number.slice(0, 3)} ${number.slice(3, 6)} ${number.slice(6)}`;
      case 'NL':
        return `${country.dialCode} ${number.slice(0, 2)} ${number.slice(2, 6)} ${number.slice(6)}`;
      case 'BE':
        return `${country.dialCode} ${number.slice(0, 3)} ${number.slice(3, 6)} ${number.slice(6)}`;
      case 'AT':
        return `${country.dialCode} ${number.slice(0, 3)} ${number.slice(3, 6)} ${number.slice(6)}`;
      case 'CH':
        return `${country.dialCode} ${number.slice(0, 2)} ${number.slice(2, 5)} ${number.slice(5)}`;
      case 'SE':
        return `${country.dialCode} ${number.slice(0, 2)} ${number.slice(2, 5)} ${number.slice(5)}`;
      case 'NO':
        return `${country.dialCode} ${number.slice(0, 3)} ${number.slice(3, 5)} ${number.slice(5)}`;
      case 'DK':
        return `${country.dialCode} ${number.slice(0, 2)} ${number.slice(2, 4)} ${number.slice(4)}`;
      case 'FI':
        return `${country.dialCode} ${number.slice(0, 2)} ${number.slice(2, 5)} ${number.slice(5)}`;
      case 'PL':
        return `${country.dialCode} ${number.slice(0, 3)} ${number.slice(3, 6)} ${number.slice(6)}`;
      case 'IE':
        return `${country.dialCode} ${number.slice(0, 2)} ${number.slice(2, 5)} ${number.slice(5)}`;
      default:
        return `${country.dialCode} ${number}`;
    }
  }

  // Parse phone number into country code and number (legacy method)
  parsePhoneNumber(input: string): PhoneNumber | null {
    // Remove all non-digit characters except +
    const cleaned = input.replace(/[^\d+]/g, '');
    
    // UK numbers
    if (cleaned.startsWith('+44')) {
      const number = cleaned.substring(3);
      if (number.length >= 9 && number.length <= 10) {
        return {
          countryCode: '+44',
          number: number,
          formatted: `+44 ${number.slice(0, 4)} ${number.slice(4, 7)} ${number.slice(7)}`,
          verified: false
        };
      }
    }
    
    // UK numbers starting with 0
    if (cleaned.startsWith('0')) {
      const number = cleaned.substring(1);
      if (number.length >= 9 && number.length <= 10) {
        return {
          countryCode: '+44',
          number: number,
          formatted: `+44 ${number.slice(0, 4)} ${number.slice(4, 7)} ${number.slice(7)}`,
          verified: false
        };
      }
    }
    
    // US numbers
    if (cleaned.startsWith('+1')) {
      const number = cleaned.substring(2);
      if (number.length === 10) {
        return {
          countryCode: '+1',
          number: number,
          formatted: `+1 (${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`,
          verified: false
        };
      }
    }
    
    return null;
  }

  // Validate phone number format
  validatePhoneNumber(phoneNumber: PhoneNumber): boolean {
    if (!phoneNumber.countryCode || !phoneNumber.number) {
      return false;
    }

    const country = this.getCountryByDialCode(phoneNumber.countryCode);
    if (!country) {
      return false;
    }

    return this.validateNumberForCountry(country, phoneNumber.number);
  }

  // Send SMS verification code (mock implementation)
  async sendVerificationCode(phoneNumber: PhoneNumber): Promise<string> {
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    const verificationId = Math.random().toString(36).substring(2, 15);
    
    const verification: SMSVerification = {
      id: verificationId,
      phoneNumber: `${phoneNumber.countryCode}${phoneNumber.number}`,
      code: code,
      expiresAt: new Date(Date.now() + 10 * 60 * 1000), // 10 minutes
      verified: false
    };

    this.verificationCodes.set(verificationId, verification);

    // In production, integrate with SMS service here
    console.log(`SMS verification code for ${phoneNumber.formatted}: ${code}`);
    
    // For demo purposes, show the code in console
    // In production, remove this and use actual SMS service
    alert(`SMS verification code: ${code}\n\nIn production, this would be sent via SMS to ${phoneNumber.formatted}`);

    return verificationId;
  }

  // Verify SMS code
  async verifyCode(verificationId: string, code: string): Promise<boolean> {
    const verification = this.verificationCodes.get(verificationId);
    
    if (!verification) {
      return false;
    }

    if (verification.expiresAt < new Date()) {
      this.verificationCodes.delete(verificationId);
      return false;
    }

    if (verification.code === code) {
      verification.verified = true;
      return true;
    }

    return false;
  }

  // Get verification status
  getVerificationStatus(verificationId: string): SMSVerification | null {
    return this.verificationCodes.get(verificationId) || null;
  }

  // Clean up expired verifications
  cleanupExpiredVerifications(): void {
    const now = new Date();
    const entries = Array.from(this.verificationCodes.entries());
    for (const [id, verification] of entries) {
      if (verification.expiresAt < now) {
        this.verificationCodes.delete(id);
      }
    }
  }
}

export const phoneService = PhoneService.getInstance(); 